import { FC, useEffect, useRef } from 'react'

import { useNavigate, useSearchParams } from 'react-router-dom'

import { useNotifications, Center } from '@sequencehq/core-components'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'

import Spinner from 'components/Loading'

/**
 * Landing page for the HubSpot v2 own-OAuth connect flow. HubSpot redirects
 * here with `code` + `state`; we complete the handshake through the generic
 * authorize endpoint (the backend stamps `integration_version=v2`). Modelled
 * on GoCardlessRedirect.
 */
export const HubspotRedirect: FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { displayNotification } = useNotifications()
  const hasRun = useRef(false)

  useEffect(() => {
    if (hasRun.current) {
      return
    }
    hasRun.current = true

    const showFailure = () => {
      displayNotification('Failed to connect to HubSpot', {
        type: 'error'
      })
      navigate('/home')
    }

    const code = searchParams.get('code')
    const state = searchParams.get('state')

    if (!code || !state) {
      showFailure()
      return
    }

    void dashboardv20240509Client
      .postAuthorizeComplete({
        service: 'HubSpot',
        code,
        state
      })
      .then(response => {
        if (response.error || !response.data) {
          showFailure()
          return
        }

        displayNotification('Successfully connected to HubSpot', {
          type: 'success'
        })
        navigate('/integration-management/HubSpot?authorized=true')
      })
      // Without this the spinner hangs forever if the request rejects (e.g. a
      // transient network failure) rather than resolving with an error.
      .catch(showFailure)
  }, [searchParams, navigate, displayNotification])

  return (
    <Center height="100vh">
      <Spinner />
    </Center>
  )
}
